import {
  Container,
  Grid,
  Box,
  Button,
  Typography,
  Fade,
  Grow,
  Slide,
  makeStyles,
} from "@material-ui/core";
import { useEffect, useState } from "react";
import Axios from "axios";
import { Helmet } from "react-helmet-async";
import { Link, useParams, Navigate } from "react-router-dom";

import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import FormatQuoteIcon from "@material-ui/icons/FormatQuote";

import { HeroParticles } from "../../components/shared/HeroParticles";
import { TypingQuote } from "../../components/shared/TypingQuote";
import { Loading } from "../../components/shared/loading";
import { resolveIcon } from "../../data/productIcons";

const useStyles = makeStyles((theme) => ({
  "@keyframes heroFadeUp": {
    from: { opacity: 0, transform: "translateY(24px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes heroExpand": {
    from: { width: 0, opacity: 0 },
    to: { width: 90, opacity: 1 },
  },
  "@keyframes heroShimmerSweep": {
    "0%": { backgroundPosition: "-200% center" },
    "100%": { backgroundPosition: "200% center" },
  },
  "@keyframes floatMark": {
    "0%, 100%": { transform: "translateY(0)" },
    "50%": { transform: "translateY(-6px)" },
  },

  // ── Hero ──
  heroSection: {
    position: "relative",
    overflow: "hidden",
    minHeight: "48vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "linear-gradient(135deg, #1a001a 0%, #2d002d 55%, #1a001a 100%)",
  },
  heroGlow: {
    position: "absolute",
    width: "60%",
    height: "80%",
    top: "-20%",
    right: "-10%",
    borderRadius: "50%",
    filter: "blur(70px)",
    pointerEvents: "none",
    zIndex: 0,
  },
  heroContent: {
    position: "relative",
    zIndex: 2,
    textAlign: "center",
    color: "#ffffff",
    padding: "44px 20px",
    maxWidth: 720,
  },
  heroMark: {
    width: 58,
    height: 58,
    borderRadius: 16,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    margin: "0 auto 14px",
    boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
    border: "1px solid rgba(255,255,255,0.22)",
    animation:
      "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) both, $floatMark 6s ease-in-out 1s infinite",
    "& svg": { fontSize: 28 },
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    fontSize: "0.64rem",
    fontWeight: 700,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    padding: "5px 12px",
    borderRadius: 20,
    color: "#ffffff",
    background: "rgba(255,255,255,0.1)",
    border: "1px solid rgba(255,255,255,0.25)",
    marginBottom: 14,
    animation: "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both",
  },
  statusDotLive: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#7CFC9B",
    boxShadow: "0 0 8px #7CFC9B",
  },
  statusDotSoon: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#f2c14e",
    boxShadow: "0 0 8px #f2c14e",
  },
  heroTitle: {
    fontSize: "clamp(1.55rem, 3.6vw, 2.15rem)",
    fontWeight: 700,
    marginBottom: 6,
    letterSpacing: "-0.02em",
    backgroundImage:
      "linear-gradient(90deg, #ffffff 0%, #e0b8e0 25%, #c77dc7 50%, #e0b8e0 75%, #ffffff 100%)",
    backgroundSize: "200% auto",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    color: "transparent",
    animation:
      "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both, $heroShimmerSweep 6s linear infinite",
  },
  heroExpansion: {
    fontSize: "clamp(0.8rem, 1.4vw, 0.92rem)",
    fontWeight: 500,
    color: "#e0b8e0",
    marginBottom: 10,
    animation: "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.28s both",
  },
  heroDivider: {
    width: 64,
    height: 2,
    background:
      "linear-gradient(90deg, transparent, #c77dc7 30%, #e0b8e0 50%, #c77dc7 70%, transparent)",
    margin: "0 auto 14px",
    borderRadius: 2,
    animation: "$heroExpand 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.4s both",
  },
  heroTagline: {
    fontSize: "clamp(0.82rem, 1.3vw, 0.9rem)",
    fontWeight: 400,
    opacity: 0.9,
    maxWidth: 520,
    margin: "0 auto 22px",
    lineHeight: 1.6,
    animation: "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.45s both",
  },
  heroActions: {
    display: "flex",
    gap: 14,
    justifyContent: "center",
    flexWrap: "wrap",
    animation: "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.6s both",
  },
  heroPrimaryBtn: {
    background: "#ffffff",
    color: "#4c004c",
    padding: "9px 20px",
    fontSize: "0.8rem",
    fontWeight: 600,
    borderRadius: 10,
    textTransform: "none",
    boxShadow: "0 10px 26px rgba(0,0,0,0.28)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    "&, &:hover, &:focus, &:active, &:visited": {
      color: "#4c004c",
      textDecoration: "none",
    },
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 16px 34px rgba(0,0,0,0.36)",
      background: "linear-gradient(135deg, #ffffff 0%, #f5e8f5 100%)",
    },
    "& .MuiButton-endIcon": {
      transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    },
    "&:hover .MuiButton-endIcon": { transform: "translateX(4px)" },
  },
  heroGhostBtn: {
    background: "transparent",
    color: "#ffffff",
    padding: "9px 18px",
    fontSize: "0.8rem",
    fontWeight: 500,
    borderRadius: 10,
    textTransform: "none",
    border: "1px solid rgba(255,255,255,0.32)",
    transition: "all 0.3s ease",
    "&, &:hover, &:focus, &:active, &:visited": {
      color: "#ffffff",
      textDecoration: "none",
    },
    "&:hover": {
      background: "rgba(255,255,255,0.08)",
      borderColor: "rgba(255,255,255,0.6)",
    },
  },
  heroBadgeRow: {
    display: "flex",
    gap: 10,
    justifyContent: "center",
    flexWrap: "wrap",
    marginTop: 16,
    animation: "$heroFadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) 0.72s both",
  },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    fontSize: "0.72rem",
    fontWeight: 500,
    color: "rgba(255,255,255,0.85)",
    "&::before": {
      content: '""',
      width: 5,
      height: 5,
      borderRadius: "50%",
      background: "#c77dc7",
      boxShadow: "0 0 7px #c77dc7",
    },
  },

  // ── Page body ──
  pageWrapper: {
    background: "#f5f3f6",
    paddingBottom: 52,
  },
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.78rem",
    color: "#6b1b6b",
    fontWeight: 500,
    textDecoration: "none",
    padding: "22px 0 6px",
    transition: "all 0.2s ease",
    "&, &:hover, &:focus, &:active, &:visited": { textDecoration: "none" },
    "&:hover": { color: "#4c004c", gap: 10 },
  },

  // ── Overview ──
  overviewCard: {
    background: "#ffffff",
    borderRadius: 18,
    boxShadow: "0 10px 38px rgba(76,0,76,0.07)",
    border: "1px solid rgba(76,0,76,0.05)",
    padding: "28px 30px 26px",
    marginTop: 8,
    [theme.breakpoints.down("sm")]: { padding: "24px 20px 22px" },
  },
  overviewLabel: {
    fontSize: "0.62rem",
    fontWeight: 700,
    color: "#9b3b9b",
    textTransform: "uppercase",
    letterSpacing: "2px",
    marginBottom: 12,
  },
  overviewText: {
    fontSize: "0.9rem",
    color: "#2a2a2a",
    lineHeight: 1.7,
    fontWeight: 400,
    [theme.breakpoints.down("sm")]: { fontSize: "0.86rem" },
  },
  highlightStrip: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 14,
    marginTop: 22,
    paddingTop: 20,
    borderTop: "1px solid rgba(76,0,76,0.08)",
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
      gap: 14,
    },
  },
  highlightItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  highlightIcon: {
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    "& svg": { fontSize: 19 },
  },
  highlightLabel: {
    fontSize: "0.76rem",
    fontWeight: 600,
    color: "#3a2a3a",
    lineHeight: 1.35,
  },

  // ── Section shells ──
  sectionHead: {
    textAlign: "center",
    maxWidth: 500,
    margin: "42px auto 22px",
  },
  sectionKicker: {
    fontSize: "0.6rem",
    fontWeight: 700,
    color: "#9b3b9b",
    textTransform: "uppercase",
    letterSpacing: "2px",
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: "1.25rem",
    fontWeight: 700,
    color: "#4c004c",
    letterSpacing: "-0.01em",
    marginBottom: 8,
  },
  sectionSubtitle: {
    fontSize: "0.82rem",
    color: "#666",
    lineHeight: 1.6,
  },

  // ── Feature cards ──
  featureCard: {
    height: "100%",
    background: "#ffffff",
    borderRadius: 14,
    padding: "20px 20px",
    border: "1px solid rgba(76,0,76,0.06)",
    boxShadow: "0 4px 18px rgba(0,0,0,0.04)",
    transition: "all 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
    "&:hover": {
      transform: "translateY(-6px)",
      boxShadow: "0 20px 44px rgba(76,0,76,0.14)",
      borderColor: "rgba(76,0,76,0.12)",
    },
    "&:hover $featureIcon": {
      transform: "scale(1.08) rotate(-4deg)",
    },
  },
  featureIcon: {
    width: 42,
    height: 42,
    borderRadius: 11,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    marginBottom: 12,
    boxShadow: "0 6px 16px rgba(76,0,76,0.22)",
    transition: "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
    "& svg": { fontSize: 21 },
  },
  featureTitle: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "#3a003a",
    marginBottom: 6,
  },
  featureDesc: {
    fontSize: "0.78rem",
    color: "#666",
    lineHeight: 1.6,
  },

  // ── Toolkit (dark band) ──
  toolkitBand: {
    position: "relative",
    marginTop: 44,
    borderRadius: 20,
    overflow: "hidden",
    background:
      "linear-gradient(160deg, #3a003a 0%, #2d002d 55%, #1a001a 100%)",
    padding: "36px 32px 34px",
    boxShadow: "0 20px 55px rgba(76,0,76,0.25)",
    [theme.breakpoints.down("sm")]: { padding: "34px 22px 30px" },
    "&::before": {
      content: '""',
      position: "absolute",
      width: 360,
      height: 360,
      borderRadius: "50%",
      top: -160,
      right: -120,
      background:
        "radial-gradient(circle, rgba(199,125,199,0.28), transparent 70%)",
      filter: "blur(40px)",
      pointerEvents: "none",
    },
  },
  toolkitHead: { position: "relative", zIndex: 1, textAlign: "center", marginBottom: 32 },
  toolkitKicker: {
    fontSize: "0.62rem",
    fontWeight: 700,
    color: "#c77dc7",
    textTransform: "uppercase",
    letterSpacing: "2px",
    marginBottom: 10,
  },
  toolkitTitle: {
    fontSize: "1.22rem",
    fontWeight: 700,
    color: "#ffffff",
    marginBottom: 8,
  },
  toolkitSubtitle: {
    fontSize: "0.86rem",
    color: "rgba(255,255,255,0.6)",
    lineHeight: 1.6,
    maxWidth: 520,
    margin: "0 auto",
  },
  toolCard: {
    position: "relative",
    zIndex: 1,
    height: "100%",
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 12,
    padding: "15px 15px",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "rgba(255,255,255,0.09)",
      borderColor: "rgba(199,125,199,0.4)",
      transform: "translateY(-4px)",
    },
  },
  toolIcon: {
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    background:
      "linear-gradient(135deg, rgba(199,125,199,0.9), rgba(155,59,155,0.9))",
    "& svg": { fontSize: 19 },
  },
  toolTitle: {
    fontSize: "0.86rem",
    fontWeight: 600,
    color: "#ffffff",
    marginBottom: 4,
  },
  toolDesc: {
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.62)",
    lineHeight: 1.5,
  },

  // ── Replaces band ──
  replacesBand: {
    marginTop: 44,
    background: "#ffffff",
    borderRadius: 20,
    border: "1px solid rgba(76,0,76,0.06)",
    boxShadow: "0 10px 38px rgba(76,0,76,0.07)",
    padding: "30px 28px 26px",
    [theme.breakpoints.down("sm")]: { padding: "30px 20px 24px" },
  },
  replacesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    gap: 14,
    marginTop: 4,
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },
  replaceItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 8,
    padding: "18px 8px",
    borderRadius: 14,
    background: "#faf6fa",
    border: "1px solid rgba(76,0,76,0.05)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    "&:hover": {
      transform: "translateY(-4px)",
      background: "#f4e9f4",
      borderColor: "rgba(76,0,76,0.12)",
    },
  },
  replaceIcon: {
    width: 38,
    height: 38,
    borderRadius: 11,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#4c004c",
    background:
      "linear-gradient(135deg, rgba(199,125,199,0.2), rgba(76,0,76,0.08))",
    "& svg": { fontSize: 20 },
  },
  replaceLabel: {
    fontSize: "0.82rem",
    fontWeight: 600,
    color: "#3a003a",
    lineHeight: 1.2,
  },
  replaceSub: {
    fontSize: "0.66rem",
    color: "#9b3b9b",
    fontStyle: "italic",
  },
  replaceResult: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    marginTop: 26,
    paddingTop: 22,
    borderTop: "1px dashed rgba(76,0,76,0.18)",
    fontSize: "0.92rem",
    fontWeight: 600,
    color: "#4c004c",
    textAlign: "center",
    "& svg": { fontSize: 20, color: "#9b3b9b" },
  },

  // ── SaaS model band ──
  saasBand: {
    position: "relative",
    marginTop: 44,
    borderRadius: 20,
    overflow: "hidden",
    padding: "34px 30px 32px",
    background:
      "linear-gradient(150deg, #f7eef7 0%, #efe1ef 55%, #f7eef7 100%)",
    border: "1px solid rgba(76,0,76,0.08)",
    [theme.breakpoints.down("sm")]: { padding: "34px 22px 30px" },
  },
  saasGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 18,
    marginTop: 8,
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: 14,
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  saasPoint: {
    background: "#ffffff",
    borderRadius: 14,
    padding: "18px 18px",
    border: "1px solid rgba(76,0,76,0.05)",
    boxShadow: "0 6px 20px rgba(76,0,76,0.06)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "0 16px 36px rgba(76,0,76,0.14)",
    },
  },
  saasIcon: {
    width: 40,
    height: 40,
    borderRadius: 11,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    marginBottom: 11,
    background: "linear-gradient(135deg, #6b1b6b, #4c004c)",
    boxShadow: "0 6px 14px rgba(76,0,76,0.22)",
    "& svg": { fontSize: 20 },
  },
  saasTitle: {
    fontSize: "0.84rem",
    fontWeight: 600,
    color: "#3a003a",
    marginBottom: 5,
  },
  saasDesc: {
    fontSize: "0.74rem",
    color: "#6a5a6a",
    lineHeight: 1.55,
  },

  // ── How it works ──
  howGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 16,
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
      gap: 16,
    },
  },
  howStep: {
    position: "relative",
    height: "100%",
    background: "#ffffff",
    borderRadius: 14,
    padding: "22px 20px 20px",
    border: "1px solid rgba(76,0,76,0.06)",
    boxShadow: "0 6px 22px rgba(76,0,76,0.06)",
    overflow: "hidden",
    transition: "all 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
    "&:hover": {
      transform: "translateY(-6px)",
      boxShadow: "0 18px 40px rgba(76,0,76,0.14)",
      borderColor: "rgba(76,0,76,0.12)",
    },
    "&:hover $howStepIcon": {
      transform: "scale(1.08) rotate(-4deg)",
    },
  },
  howStepNum: {
    position: "absolute",
    top: 12,
    right: 16,
    fontSize: "2.1rem",
    fontWeight: 800,
    lineHeight: 1,
    color: "rgba(76,0,76,0.07)",
    letterSpacing: "-0.03em",
  },
  howStepIcon: {
    position: "relative",
    zIndex: 1,
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    marginBottom: 13,
    boxShadow: "0 6px 16px rgba(76,0,76,0.22)",
    transition: "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
    "& svg": { fontSize: 22 },
  },
  howStepTitle: {
    position: "relative",
    zIndex: 1,
    fontSize: "0.88rem",
    fontWeight: 600,
    color: "#3a003a",
    marginBottom: 6,
  },
  howStepDesc: {
    position: "relative",
    zIndex: 1,
    fontSize: "0.78rem",
    color: "#666",
    lineHeight: 1.6,
  },

  // ── Platforms ──
  platformBand: {
    marginTop: 44,
    background: "#ffffff",
    borderRadius: 20,
    border: "1px solid rgba(76,0,76,0.06)",
    boxShadow: "0 10px 34px rgba(76,0,76,0.07)",
    padding: "28px 28px",
    textAlign: "center",
  },
  platformLinks: {
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
    justifyContent: "center",
    marginTop: 22,
  },
  platformLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 18px",
    background: "#f6eef6",
    border: "1px solid rgba(76,0,76,0.1)",
    borderRadius: 11,
    textDecoration: "none",
    color: "#4c004c",
    fontSize: "0.8rem",
    fontWeight: 600,
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    "&, &:hover, &:focus, &:active, &:visited": { textDecoration: "none" },
    "&:hover": {
      background: "linear-gradient(135deg, #4c004c, #6b1b6b)",
      color: "#ffffff",
      transform: "translateY(-3px)",
      boxShadow: "0 12px 26px rgba(76,0,76,0.3)",
    },
    "& svg": { fontSize: 20 },
  },

  // ── Quote ──
  quoteSection: {
    position: "relative",
    marginTop: 44,
    background: "linear-gradient(135deg, #6b1b6b 0%, #4a0e4a 100%)",
    borderRadius: 18,
    padding: "34px 30px 32px",
    textAlign: "center",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      width: 300,
      height: 300,
      borderRadius: "50%",
      top: -150,
      left: -90,
      background:
        "radial-gradient(circle, rgba(199,125,199,0.28), transparent 70%)",
      pointerEvents: "none",
    },
  },
  quoteMark: {
    position: "relative",
    zIndex: 1,
    color: "rgba(255,255,255,0.35)",
    fontSize: 34,
    marginBottom: 2,
  },
  quoteText: {
    position: "relative",
    zIndex: 1,
    fontSize: "0.9rem",
    color: "#ffffff",
    lineHeight: 1.7,
    fontStyle: "italic",
    maxWidth: 720,
    margin: "0 auto 14px",
  },
  quoteAuthor: {
    position: "relative",
    zIndex: 1,
    color: "#ffffff",
    fontSize: "0.8rem",
    fontWeight: 600,
    opacity: 0.9,
  },

  // ── CTA ──
  // ── Categories (ESAY) ──
  categoryCard: {
    height: "100%",
    display: "flex",
    gap: 14,
    alignItems: "flex-start",
    background: "#ffffff",
    border: "1px solid rgba(76,0,76,0.08)",
    borderRadius: 14,
    padding: "18px 18px",
    boxShadow: "0 6px 22px rgba(0,0,0,0.04)",
    transition:
      "transform 0.3s cubic-bezier(0.22,1,0.36,1), box-shadow 0.3s ease, border-color 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 18px 40px rgba(76,0,76,0.14)",
      borderColor: "rgba(76,0,76,0.14)",
    },
  },
  categoryIcon: {
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: 11,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    boxShadow: "0 6px 16px rgba(76,0,76,0.22)",
    "& svg": { fontSize: 21 },
  },
  categoryTitle: {
    fontSize: "0.9rem",
    fontWeight: 700,
    color: "#2d002d",
    marginBottom: 5,
  },
  categoryDesc: {
    fontSize: "0.8rem",
    color: "#666666",
    lineHeight: 1.55,
  },

  // ── Awards (ESAY) ──
  awardCard: {
    position: "relative",
    height: "100%",
    textAlign: "center",
    background: "linear-gradient(180deg, #ffffff 0%, #faf6fa 100%)",
    border: "1px solid rgba(76,0,76,0.10)",
    borderRadius: 16,
    padding: "26px 20px 22px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
    overflow: "hidden",
    transition:
      "transform 0.35s cubic-bezier(0.22,1,0.36,1), box-shadow 0.35s ease",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: "linear-gradient(90deg, #e6b800, #f2c14e, #e6b800)",
    },
    "&:hover": {
      transform: "translateY(-6px)",
      boxShadow: "0 22px 46px rgba(76,0,76,0.18)",
    },
  },
  awardIcon: {
    width: 50,
    height: 50,
    margin: "0 auto 13px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#4c004c",
    background: "linear-gradient(135deg, #fbeeb5, #f2c14e)",
    boxShadow: "0 8px 20px rgba(242,193,78,0.4)",
    "& svg": { fontSize: 25 },
  },
  awardTitle: {
    fontSize: "0.92rem",
    fontWeight: 700,
    color: "#2d002d",
    marginBottom: 6,
    lineHeight: 1.3,
  },
  awardOrg: {
    fontSize: "0.82rem",
    color: "#666666",
    lineHeight: 1.55,
    marginBottom: 14,
    minHeight: 40,
  },
  awardMeta: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#9b3b9b",
  },
  awardYear: {
    color: "#4c004c",
  },
  awardDot: {
    color: "#c77dc7",
  },

  ctaSection: {
    position: "relative",
    background:
      "linear-gradient(160deg, #4c004c 0%, #2d002d 60%, #1a001a 100%)",
    borderRadius: 18,
    padding: "36px 30px 34px",
    marginTop: 28,
    textAlign: "center",
    overflow: "hidden",
    boxShadow: "0 14px 44px rgba(76,0,76,0.25)",
    "&::before": {
      content: '""',
      position: "absolute",
      width: 360,
      height: 360,
      borderRadius: "50%",
      top: -140,
      right: -140,
      background:
        "radial-gradient(circle, rgba(199,125,199,0.4), transparent 70%)",
      filter: "blur(50px)",
      pointerEvents: "none",
    },
  },
  ctaLabel: {
    position: "relative",
    zIndex: 1,
    fontSize: "0.62rem",
    fontWeight: 700,
    color: "#c77dc7",
    textTransform: "uppercase",
    letterSpacing: "2.2px",
    marginBottom: 8,
  },
  ctaTitle: {
    position: "relative",
    zIndex: 1,
    fontSize: "1.28rem",
    fontWeight: 700,
    color: "#ffffff",
    lineHeight: 1.3,
    maxWidth: 460,
    margin: "0 auto 10px",
    [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
  },
  ctaNote: {
    position: "relative",
    zIndex: 1,
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.65)",
    maxWidth: 420,
    margin: "0 auto 22px",
    lineHeight: 1.6,
  },
  ctaButton: {
    position: "relative",
    zIndex: 1,
    background: "#ffffff",
    color: "#4c004c",
    padding: "11px 26px",
    fontSize: "0.82rem",
    fontWeight: 600,
    borderRadius: 11,
    textTransform: "none",
    boxShadow: "0 8px 22px rgba(0,0,0,0.18)",
    transition: "all 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    "&, &:hover, &:focus, &:active, &:visited": {
      color: "#4c004c",
      textDecoration: "none",
    },
    "&:hover": {
      background: "linear-gradient(135deg, #ffffff 0%, #f5e8f5 100%)",
      transform: "translateY(-2px)",
      boxShadow: "0 14px 32px rgba(0,0,0,0.28)",
    },
    "& .MuiButton-endIcon": {
      transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    },
    "&:hover .MuiButton-endIcon": { transform: "translateX(4px)" },
  },
}));

export const ProductDetails = () => {
  const classes = useStyles();
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    Axios.get(`/api/products/${slug}`)
      .then((res) => {
        if (!active) return;
        if (res.data && res.data.status && res.data.data) {
          setProduct(res.data.data);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => {
        if (active) setNotFound(true);
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
        const anchor = document.querySelector("#back-to-top-anchor");
        if (anchor) anchor.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    return () => {
      active = false;
    };
  }, [slug]);

  if (notFound) {
    return <Navigate to="/our-products" replace />;
  }
  if (loading || !product) {
    return <Loading />;
  }

  const {
    name,
    expansion,
    category,
    accent,
    status,
    statusLabel,
    heroBadges,
    tagline,
    overview,
    highlights,
    featuresTitle,
    featuresSubtitle,
    features,
    categories,
    awards,
    replaces,
    toolkit,
    howItWorks,
    saasModel,
    platforms,
    quote,
    cta,
  } = product;
  const MarkIcon = resolveIcon(product.icon);

  const isLive = status === "live";
  const available = status === "live" || status === "saas";
  const accentGradient = `linear-gradient(135deg, ${accent.from} 0%, ${accent.to} 100%)`;

  // Primary hero / CTA action can be an external link (href) or an internal route (to).
  // When the CTA points at the contact page, carry the product name so the
  // enquiry form pre-fills its subject.
  const contactTo =
    cta.to && cta.to.includes("/get-in-touch")
      ? `/get-in-touch?subject=${encodeURIComponent(name)}`
      : cta.to;
  const primaryActionProps = cta.href
    ? { component: "a", href: cta.href, target: "_blank", rel: "noopener noreferrer" }
    : { component: Link, to: contactTo };

  return (
    <div>
      <Helmet>
        <title>{`${name} | Arawinz Soft Solutions`}</title>
        <meta name="description" content={tagline} />
      </Helmet>

      {/* Hero */}
      <Box className={classes.heroSection}>
        <HeroParticles id={`product-${slug}-particles`} />
        <Box
          className={classes.heroGlow}
          style={{
            background: `radial-gradient(circle, ${accent.soft}55, transparent 70%)`,
          }}
        />
        <Box className={classes.heroContent}>
          <Box
            className={classes.heroMark}
            style={{ background: product.logo ? "#ffffff" : accentGradient, padding: product.logo ? 6 : undefined }}
          >
            {product.logo ? (
              <img
                src={product.logo}
                alt={name}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            ) : (
              <MarkIcon />
            )}
          </Box>
          <Box className={classes.statusPill}>
            <span className={available ? classes.statusDotLive : classes.statusDotSoon} />
            {statusLabel}
          </Box>
          <Typography className={classes.heroTitle}>{name}</Typography>
          <Typography className={classes.heroExpansion}>{expansion}</Typography>
          <Box className={classes.heroDivider} />
          <Typography className={classes.heroTagline}>{tagline}</Typography>
          <Box className={classes.heroActions}>
            <Button
              {...primaryActionProps}
              className={classes.heroPrimaryBtn}
              endIcon={<ArrowForwardIcon style={{ fontSize: 16 }} />}
            >
              {cta.label}
            </Button>
            <Button
              component={Link}
              to="/our-products"
              className={classes.heroGhostBtn}
            >
              All products
            </Button>
          </Box>
          {heroBadges && (
            <Box className={classes.heroBadgeRow}>
              {heroBadges.map((b, i) => (
                <span className={classes.heroBadge} key={i}>
                  {b}
                </span>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Body */}
      <Box className={classes.pageWrapper}>
        <Container maxWidth="md">
          <Link to="/our-products" className={classes.backLink}>
            ← Back to Products
          </Link>

          {/* Overview */}
          <Fade in={true} timeout={900}>
            <Box className={classes.overviewCard}>
              <Typography className={classes.overviewLabel}>
                {category}
              </Typography>
              <Typography className={classes.overviewText}>{overview}</Typography>

              <Box className={classes.highlightStrip}>
                {highlights.map((h, i) => {
                  const HIcon = resolveIcon(h.icon);
                  return (
                    <Box className={classes.highlightItem} key={i}>
                      <Box
                        className={classes.highlightIcon}
                        style={{ background: accentGradient }}
                      >
                        <HIcon />
                      </Box>
                      <Typography className={classes.highlightLabel}>
                        {h.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Fade>

          {/* Features */}
          {features && features.length > 0 && (
            <>
              <Box className={classes.sectionHead}>
                <Typography className={classes.sectionKicker}>Features</Typography>
                <Typography className={classes.sectionTitle}>
                  {featuresTitle}
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  {featuresSubtitle}
                </Typography>
              </Box>

              <Grid container spacing={3} alignItems="stretch">
                {features.map((f, i) => {
                  const FIcon = resolveIcon(f.icon);
                  return (
                    <Grid item xs={12} sm={6} key={i}>
                      <Grow in={true} timeout={600 + i * 100}>
                        <Box className={classes.featureCard}>
                          <Box
                            className={classes.featureIcon}
                            style={{ background: accentGradient }}
                          >
                            <FIcon />
                          </Box>
                          <Typography className={classes.featureTitle}>
                            {f.title}
                          </Typography>
                          <Typography className={classes.featureDesc}>
                            {f.desc}
                          </Typography>
                        </Box>
                      </Grow>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}

          {/* Categories (ESAY): what you find inside */}
          {categories && (
            <>
              <Box className={classes.sectionHead}>
                <Typography className={classes.sectionKicker}>
                  What's inside
                </Typography>
                <Typography className={classes.sectionTitle}>
                  {categories.title}
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  {categories.subtitle}
                </Typography>
              </Box>
              <Grid container spacing={3} alignItems="stretch">
                {categories.items.map((c, i) => {
                  const CIcon = resolveIcon(c.icon);
                  return (
                    <Grid item xs={12} sm={6} key={i}>
                      <Grow in={true} timeout={600 + i * 100}>
                        <Box className={classes.categoryCard}>
                          <Box
                            className={classes.categoryIcon}
                            style={{ background: accentGradient }}
                          >
                            <CIcon />
                          </Box>
                          <Box>
                            <Typography className={classes.categoryTitle}>
                              {c.title}
                            </Typography>
                            <Typography className={classes.categoryDesc}>
                              {c.desc}
                            </Typography>
                          </Box>
                        </Box>
                      </Grow>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}

          {/* Awards (ESAY): recognition */}
          {awards && (
            <>
              <Box className={classes.sectionHead}>
                <Typography className={classes.sectionKicker}>
                  Recognition
                </Typography>
                <Typography className={classes.sectionTitle}>
                  {awards.title}
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  {awards.subtitle}
                </Typography>
              </Box>
              <Grid
                container
                spacing={3}
                alignItems="stretch"
                justifyContent="center"
              >
                {awards.items.map((a, i) => {
                  const AIcon = resolveIcon(a.icon);
                  return (
                    <Grid item xs={12} sm={4} key={i}>
                      <Grow in={true} timeout={600 + i * 140}>
                        <Box className={classes.awardCard}>
                          <Box className={classes.awardIcon}>
                            <AIcon />
                          </Box>
                          <Typography className={classes.awardTitle}>
                            {a.title}
                          </Typography>
                          <Typography className={classes.awardOrg}>
                            {a.org}
                          </Typography>
                          <Box className={classes.awardMeta}>
                            <span className={classes.awardYear}>{a.year}</span>
                            <span className={classes.awardDot}>&bull;</span>
                            <span>{a.place}</span>
                          </Box>
                        </Box>
                      </Grow>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}

          {/* Replaces band (Vault) */}
          {replaces && (
            <Fade in={true} timeout={900}>
              <Box className={classes.replacesBand}>
                <Box className={classes.sectionHead} style={{ margin: "0 auto 26px" }}>
                  <Typography className={classes.sectionKicker}>
                    Consolidate
                  </Typography>
                  <Typography className={classes.sectionTitle}>
                    {replaces.title}
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    {replaces.subtitle}
                  </Typography>
                </Box>
                <Box className={classes.replacesGrid}>
                  {replaces.items.map((r, i) => {
                    const RIcon = resolveIcon(r.icon);
                    return (
                      <Box className={classes.replaceItem} key={i}>
                        <Box className={classes.replaceIcon}>
                          <RIcon />
                        </Box>
                        <Typography className={classes.replaceLabel}>
                          {r.label}
                        </Typography>
                        {r.sub ? (
                          <Typography className={classes.replaceSub}>
                            {r.sub}
                          </Typography>
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
                <Box className={classes.replaceResult}>
                  <MarkIcon />
                  {replaces.resultLabel}
                </Box>
              </Box>
            </Fade>
          )}

          {/* Toolkit (Vault) */}
          {toolkit && (
            <Slide direction="up" in={true} timeout={700}>
              <Box className={classes.toolkitBand}>
                <Box className={classes.toolkitHead}>
                  <Typography className={classes.toolkitKicker}>
                    Bundled in
                  </Typography>
                  <Typography className={classes.toolkitTitle}>
                    {toolkit.title}
                  </Typography>
                  <Typography className={classes.toolkitSubtitle}>
                    {toolkit.subtitle}
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  {toolkit.items.map((t, i) => {
                    const TIcon = resolveIcon(t.icon);
                    return (
                      <Grid item xs={12} sm={6} md={4} key={i}>
                        <Box className={classes.toolCard}>
                          <Box className={classes.toolIcon}>
                            <TIcon />
                          </Box>
                          <Box>
                            <Typography className={classes.toolTitle}>
                              {t.title}
                            </Typography>
                            <Typography className={classes.toolDesc}>
                              {t.desc}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Slide>
          )}

          {/* SaaS model band (Vault + Coms) */}
          {saasModel && (
            <Slide direction="up" in={true} timeout={700}>
              <Box className={classes.saasBand}>
                <Box className={classes.sectionHead} style={{ margin: "0 auto 24px" }}>
                  <Typography className={classes.sectionKicker}>
                    How you get it
                  </Typography>
                  <Typography className={classes.sectionTitle}>
                    {saasModel.title}
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    {saasModel.subtitle}
                  </Typography>
                </Box>
                <Box className={classes.saasGrid}>
                  {saasModel.points.map((p, i) => {
                    const PIcon = resolveIcon(p.icon);
                    return (
                      <Box className={classes.saasPoint} key={i}>
                        <Box className={classes.saasIcon}>
                          <PIcon />
                        </Box>
                        <Typography className={classes.saasTitle}>
                          {p.title}
                        </Typography>
                        <Typography className={classes.saasDesc}>
                          {p.desc}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Slide>
          )}

          {/* How it works (LeadGen) */}
          {howItWorks && (
            <>
              <Box className={classes.sectionHead}>
                <Typography className={classes.sectionKicker}>
                  How it works
                </Typography>
                <Typography className={classes.sectionTitle}>
                  {howItWorks.title}
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  {howItWorks.subtitle}
                </Typography>
              </Box>
              <Box className={classes.howGrid}>
                {howItWorks.steps.map((s, i) => {
                  const SIcon = resolveIcon(s.icon);
                  return (
                    <Grow in={true} timeout={600 + i * 150} key={i}>
                      <Box className={classes.howStep}>
                        <span className={classes.howStepNum}>
                          {`0${i + 1}`}
                        </span>
                        <Box
                          className={classes.howStepIcon}
                          style={{ background: accentGradient }}
                        >
                          <SIcon />
                        </Box>
                        <Typography className={classes.howStepTitle}>
                          {s.title}
                        </Typography>
                        <Typography className={classes.howStepDesc}>
                          {s.desc}
                        </Typography>
                      </Box>
                    </Grow>
                  );
                })}
              </Box>
            </>
          )}

          {/* Platforms (ESAY) */}
          {platforms && (
            <Fade in={true} timeout={900}>
              <Box className={classes.platformBand}>
                <Typography className={classes.sectionKicker}>
                  Get it now
                </Typography>
                <Typography className={classes.sectionTitle}>
                  Available on your platform
                </Typography>
                <Box className={classes.platformLinks}>
                  {platforms.map((p, i) => {
                    const PIcon = resolveIcon(p.icon);
                    return (
                      <a
                        key={i}
                        href={p.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.platformLink}
                      >
                        <PIcon />
                        {p.label}
                      </a>
                    );
                  })}
                </Box>
              </Box>
            </Fade>
          )}

          {/* Founder quote */}
          {quote && (
            <Box className={classes.quoteSection}>
              <FormatQuoteIcon className={classes.quoteMark} />
              <TypingQuote text={`"${quote}"`} className={classes.quoteText} />
              <Typography className={classes.quoteAuthor}>
                - Chethan. P. S. S, Managing Director
              </Typography>
            </Box>
          )}

          {/* CTA */}
          <Box className={classes.ctaSection}>
            <Typography className={classes.ctaLabel}>
              {isLive
                ? "Ready when you are"
                : status === "soon"
                ? "On the way"
                : "Bring it to your team"}
            </Typography>
            <Typography className={classes.ctaTitle}>
              {isLive
                ? `Start using ${name} today.`
                : status === "soon"
                ? `${name} is coming soon.`
                : `Set up ${name} for your team.`}
            </Typography>
            <Typography className={classes.ctaNote}>{cta.note}</Typography>
            <Button
              {...primaryActionProps}
              className={classes.ctaButton}
              endIcon={<ArrowForwardIcon style={{ fontSize: 16 }} />}
            >
              {cta.label}
            </Button>
          </Box>
        </Container>
      </Box>
    </div>
  );
};
